import React from "react";
import { Link } from "react-router-dom";
import { X } from "lucide-react";
import { useQuery } from "@tanstack/react-query";

const BASE = process.env.REACT_APP_BACKEND_URL;
const DISMISS_KEY = "rhm-dismissed-banners";

const readDismissed = () => {
    try { return JSON.parse(localStorage.getItem(DISMISS_KEY) || "[]"); } catch { return []; }
};

// Scheduled promo banners. The backend only returns banners inside their live window.
export default function SiteBanners({ placement = "top_bar", className = "" }) {
    const [dismissed, setDismissed] = React.useState(readDismissed);
    const { data: banners = [] } = useQuery({
        queryKey: ["site-banners", placement],
        queryFn: async () => (await fetch(`${BASE}/api/site/banners?placement=${placement}`)).json(),
        staleTime: 60000,
    });

    const visible = banners.filter((b) => !dismissed.includes(b.id));

    React.useEffect(() => {
        if (placement !== "top_bar") return;
        document.body.classList.toggle("banner-on", visible.length > 0);
        return () => document.body.classList.remove("banner-on");
    }, [placement, visible.length]);

    if (!visible.length) return null;

    const hide = (id) => {
        const next = [...dismissed, id];
        setDismissed(next);
        localStorage.setItem(DISMISS_KEY, JSON.stringify(next));
    };

    const tones = {
        gold: "banner-gold",
        lime: "banner-lime",
        dark: "bg-bg1 text-white border-y border-white/10",
        outline: "bg-transparent text-gold border-y border-gold/40",
    };

    return (
        <div className={className} data-testid={`site-banners-${placement}`}>
            {visible.map((b) => (
                <div key={b.id} data-testid={`site-banner-${b.id}`}
                    className={`relative px-6 py-2.5 ${tones[b.tone] || tones.gold}`}>
                    <div className="mx-auto max-w-7xl flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-center
                                    mono text-[10px] md:text-[11px] tracking-[0.14em] uppercase">
                        {b.headline && <strong className="font-bold">{b.headline}</strong>}
                        <span className="normal-case tracking-normal text-sm md:text-[13px]">{b.message}</span>
                        {b.link_label && (
                            <Link to={b.link_href || "/"} data-testid={`site-banner-link-${b.id}`}
                                className="underline underline-offset-4 font-bold">{b.link_label}</Link>
                        )}
                    </div>
                    {b.dismissible !== false && (
                        <button onClick={() => hide(b.id)} aria-label="Dismiss banner"
                            data-testid={`site-banner-dismiss-${b.id}`}
                            className="absolute right-3 top-1/2 -translate-y-1/2 opacity-70 hover:opacity-100">
                            <X className="w-3.5 h-3.5" />
                        </button>
                    )}
                </div>
            ))}
        </div>
    );
}
